import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import { BusinessConfigData, RawBusinessConfig } from "../../interfaces/BusinessObject";
import { convertRawBusinessConfig } from "../converters/backendToUi/businessConfigConvertor";
import { buildUpdateBusinessConfigPayload } from "../converters/uiToBackend/BusinessConfigConvertor";
import {
  extractApiErrorMessage,
  fetchOrThrowFriendly,
  throwIfNotOk,
} from "../helpers/ApiErrorHelper";
import { getRequest, putRequest, deleteRequest } from "../http/httpRequest";

function configEndpoint(businessId: string): string {
  return `${ServiceEndpoints.BUSINESS_BY_ID}/${encodeURIComponent(businessId)}/config`;
}

/**
 * NineBusinessRs<Map<String,Object>> always comes back HTTP 200 from this
 * controller (the methods return the envelope directly, not a
 * ResponseEntity) — `throwIfNotOk` above only catches a gateway-level
 * failure (502/503/504, no body). The real success/failure signal is
 * `generalProcessingStatus.processingStatus` ("OK" / "ERROR") — same
 * envelope shape updateBusinessMoreDetails already checks.
 */
function throwOnConfigFailure(data: unknown, fallback: string): void {
  const status = (data as { generalProcessingStatus?: { processingStatus?: string } } | null)
    ?.generalProcessingStatus?.processingStatus;
  if (status === "OK") return;
  throw new Error(extractApiErrorMessage(data) || fallback);
}

function contentsFirst(data: unknown): RawBusinessConfig | undefined {
  const contents = (data as { contents?: unknown[] } | null)?.contents;
  const first = Array.isArray(contents) ? contents[0] : undefined;
  return first && typeof first === "object" ? (first as RawBusinessConfig) : undefined;
}

/**
 * GET .../config — public on the backend (@SecurityRequirements), but we
 * still pass a bearer token when there is one, same as getBusinessById.
 * A business whose config row hasn't been created yet still comes back OK
 * with an empty/partial map (lazy row creation lives server-side) — not an
 * error case here.
 */
async function getBusinessConfig(businessId: string): Promise<BusinessConfigData> {
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() =>
    getRequest(configEndpoint(businessId), includeBearerToken)
  );
  await throwIfNotOk(response, "Couldn't load business config.");
  const data = await response.json().catch(() => null);
  throwOnConfigFailure(data, "Couldn't load business config.");
  return convertRawBusinessConfig(contentsFirst(data));
}

/**
 * PUT .../config — partial update: only the keys present in `fields` are
 * sent, and only those columns are written server-side. Each key present
 * fully REPLACES that column (no server merge) — see
 * buildUpdateBusinessConfigPayload's doc comment. Returns the server's full
 * post-save config as the source of truth, same as updateBusinessMoreDetails.
 */
async function updateBusinessConfig(
  businessId: string,
  fields: Partial<BusinessConfigData>
): Promise<BusinessConfigData> {
  const body = buildUpdateBusinessConfigPayload(fields);
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => putRequest(configEndpoint(businessId), body, includeBearerToken));
  await throwIfNotOk(response, "Couldn't save business config.");
  const data = await response.json().catch(() => null);
  throwOnConfigFailure(data, "Couldn't save business config.");
  return convertRawBusinessConfig(contentsFirst(data));
}

/**
 * DELETE .../config — soft-disables every field (is_enabled=false) without
 * touching any stored value, so re-enabling never requires reconfiguring
 * from scratch. Returns the server's full post-disable config.
 */
async function disableBusinessConfig(businessId: string): Promise<BusinessConfigData> {
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => deleteRequest(configEndpoint(businessId), includeBearerToken));
  await throwIfNotOk(response, "Couldn't disable business config.");
  const data = await response.json().catch(() => null);
  throwOnConfigFailure(data, "Couldn't disable business config.");
  return convertRawBusinessConfig(contentsFirst(data));
}

const BusinessConfigApiService = {
  getBusinessConfig,
  updateBusinessConfig,
  disableBusinessConfig,
};

export default BusinessConfigApiService;
